import { defineStore } from "pinia";
import { ref } from "vue";
import { authFetch } from "@/api/auth-fetch";

/**
 * Asiakasportaalista tulevat palvelupyynnöt.
 *
 * Pyyntö EI ole sopimusmuutos vaan työjonorivi: asiakas pyytää,
 * henkilökunta päättää ja toteuttaa muutoksen normaaleilla
 * sopimustoiminnoilla. Siksi tässä storessa ei ole mitään joka
 * kirjoittaisi sopimukselle.
 */

const BASE = "/api/v1/service-requests";

export type ServiceRequestType = "SEAT_CHANGE" | "CANCEL";
export type ServiceRequestStatus = "OPEN" | "RESOLVED" | "REJECTED";

export interface ServiceRequest {
  id: string;
  brandId: string;
  customerId: string;
  contractId: string;
  contractLineId: string;
  type: ServiceRequestType;
  status: ServiceRequestStatus;
  requestedQuantity: number | null;
  requestedDate: string | null;
  note: string | null;
  /** Rivin määrä pyynnön saapuessa — mistä asiakas halusi muuttaa. */
  quantityAtRequest: number;
  resolvedByUserId: string | null;
  resolvedAt: string | null;
  resolutionNote: string | null;
  createdAt: string;
  updatedAt: string;
}

interface Paginated<T> {
  data: T[];
  meta: { page: number; pageSize: number; total: number };
}

/**
 * Rivin konteksti työjononäkymää varten.
 *
 * Palvelupyyntörivi kantaa pelkkiä id:itä — se riittää
 * sopimuskohtaiselle paneelille, jossa asiakas ja sopimus ovat jo
 * ruudulla, mutta ei koko brändin kattavalle listalle: "10 → 12" ei
 * kerro mitään ilman tietoa kenestä ja mistä sopimuksesta on kyse.
 * Konteksti haetaan siksi sopimuslistauksesta, joka palauttaa
 * sopimusnumeron, otsikon JA upotetun asiakkaan yhdellä kutsulla.
 */
export interface ServiceRequestContext {
  contractNumber: number;
  contractTitle: string | null;
  customerName: string;
  customerNumber: number | null;
}

/** Sopimuslistauksen rivi siltä osin kuin konteksti tarvitsee. */
interface ContractListRow {
  id: string;
  contractNumber: number;
  customerId: string;
  title: string | null;
  customer?: { id: string; name: string; customerNumber?: number };
}

/** Työjonon hakuehdot — vain ne joita API tukee. */
export interface ServiceRequestQuery {
  status?: ServiceRequestStatus;
  customerId?: string;
  page?: number;
  pageSize?: number;
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(url, {
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });
  const body = (await res.json().catch(() => null)) as
    | { data?: T; message?: string | string[] }
    | null;
  if (!res.ok) {
    throw new Error(
      Array.isArray(body?.message)
        ? body.message.join(", ")
        : (body?.message ?? `HTTP ${res.status}`),
    );
  }
  return (body as { data: T }).data;
}

export const useServiceRequestsStore = defineStore("serviceRequests", () => {
  /** Sopimuskohtainen välimuisti, jotta paneeli ei hae muiden rivejä. */
  const byContract = ref<Record<string, ServiceRequest[]>>({});
  const loading = ref(false);
  const error = ref<string | null>(null);

  /**
   * Hakee yhden sopimuksen pyynnöt. Palvelin ei tarjoa
   * `contractId`-suodatinta (pyyntö on rivikohtainen), joten rajaus
   * tehdään asiakkaalla ja suodatetaan sopimukselle tässä. Määrät ovat
   * pieniä — yksi asiakas, yksi sivullinen.
   */
  async function fetchForContract(
    contractId: string,
    customerId: string,
  ): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const params = new URLSearchParams({
        customerId,
        pageSize: "100",
      });
      const res = await authFetch(`${BASE}?${params.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = (await res.json()) as Paginated<ServiceRequest>;
      byContract.value = {
        ...byContract.value,
        [contractId]: (body.data ?? []).filter(
          (r) => r.contractId === contractId,
        ),
      };
    } catch (e) {
      error.value = (e as Error).message;
      byContract.value = { ...byContract.value, [contractId]: [] };
    } finally {
      loading.value = false;
    }
  }

  /**
   * Merkitsee pyynnön käsitellyksi. EI toteuta muutosta sopimukselle —
   * käsittelijä tekee sen erikseen ja merkitsee pyynnön vasta sitten.
   * Heittää virheen, jotta näkymä voi näyttää toastin.
   */
  async function resolve(
    id: string,
    contractId: string,
    customerId: string,
    payload: { status: "RESOLVED" | "REJECTED"; resolutionNote?: string },
  ): Promise<void> {
    await request<ServiceRequest>(`${BASE}/${id}/resolve`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
    await fetchForContract(contractId, customerId);
  }

  function forContract(contractId: string): ServiceRequest[] {
    return byContract.value[contractId] ?? [];
  }

  // ─── Työjononäkymä (/service-requests) ────────────────────────────
  //
  // Oma tilansa sopimuspaneelin rinnalle: paneeli näyttää yhden
  // sopimuksen rivit ja jää ennalleen, tämä kattaa koko brändin.
  // Yhteinen `loading`/`error` sotkisi kummankin, kun sopimussivulla
  // molemmat voivat olla auki.

  const queue = ref<ServiceRequest[]>([]);
  const queueMeta = ref({ page: 1, pageSize: 25, total: 0 });
  const queueLoading = ref(false);
  const queueError = ref<string | null>(null);

  /** contractId → rivin konteksti. Täytetään `hydrateContext`illa. */
  const context = ref<Record<string, ServiceRequestContext>>({});
  /** Asiakkaat joiden sopimukset on jo haettu — ei haeta toiste. */
  const contextFetched = new Set<string>();

  /**
   * Avoimien pyyntöjen määrä navigaation merkkiä varten.
   *
   * Erillinen `queueMeta.total`ista: navigaatio näyttää aina avoimet,
   * myös silloin kun näkymässä on jokin muu suodatin päällä (tai kun
   * näkymää ei ole avattu lainkaan).
   */
  const openCount = ref(0);
  let pollHandle: ReturnType<typeof setInterval> | null = null;

  /** Henkilökunnan listaus. Palvelin järjestää: avoimet ja vanhimmat ensin. */
  async function fetchQueue(query: ServiceRequestQuery = {}): Promise<void> {
    queueLoading.value = true;
    queueError.value = null;
    try {
      const params = new URLSearchParams();
      if (query.status) params.set("status", query.status);
      if (query.customerId) params.set("customerId", query.customerId);
      params.set("page", String(query.page ?? 1));
      params.set("pageSize", String(query.pageSize ?? 25));

      const res = await authFetch(`${BASE}?${params.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = (await res.json()) as Paginated<ServiceRequest>;
      queue.value = body.data ?? [];
      queueMeta.value = body.meta ?? {
        page: 1,
        pageSize: 25,
        total: queue.value.length,
      };
      await hydrateContext(queue.value);
    } catch (e) {
      queueError.value = (e as Error).message;
      queue.value = [];
      queueMeta.value = { page: 1, pageSize: 25, total: 0 };
    } finally {
      queueLoading.value = false;
    }
  }

  /**
   * Hakee näytettävien rivien asiakas- ja sopimustiedot.
   *
   * Haku tehdään ASIAKKAITTAIN eikä sopimuksittain: yksi
   * `?customerId=`-kutsu palauttaa kaikki sen asiakkaan sopimukset
   * upotettuine asiakastietoineen, joten yhden asiakkaan monta pyyntöä
   * maksaa yhden kutsun eikä montaa. Sivullinen työjonoa koskee
   * käytännössä muutamaa asiakasta.
   *
   * Epäonnistuminen on hiljainen: konteksti on rivin koriste, ei sen
   * sisältö — näkymä osaa näyttää rivin ilmankin, ja pyynnön
   * katoaminen listalta olisi pahempi vika kuin puuttuva nimi.
   */
  async function hydrateContext(rows: ServiceRequest[]): Promise<void> {
    const missing = [
      ...new Set(
        rows
          .map((r) => r.customerId)
          .filter((id) => !contextFetched.has(id)),
      ),
    ];
    if (!missing.length) return;

    const results = await Promise.all(
      missing.map(async (customerId) => {
        try {
          // pageSize 200 = API:n maksimi. Yli 200 sopimuksen asiakkaalla
          // yksittäinen rivi voi jäädä ilman kontekstia; se näkyy
          // näkymässä viivana eikä kaada mitään.
          const res = await authFetch(
            `/api/v1/contracts?customerId=${encodeURIComponent(customerId)}` +
              `&pageSize=200`,
          );
          if (!res.ok) return [];
          const body = (await res.json()) as Paginated<ContractListRow>;
          return body.data ?? [];
        } catch {
          return [];
        }
      }),
    );

    const next = { ...context.value };
    for (const contract of results.flat()) {
      next[contract.id] = {
        contractNumber: contract.contractNumber,
        contractTitle: contract.title ?? null,
        customerName: contract.customer?.name ?? "",
        customerNumber: contract.customer?.customerNumber ?? null,
      };
    }
    context.value = next;
    for (const id of missing) contextFetched.add(id);
  }

  function contextFor(contractId: string): ServiceRequestContext | null {
    return context.value[contractId] ?? null;
  }

  /**
   * Avoimien määrä navigaatiolle. Kysyy vain yhden rivin — kiinnostava
   * on `meta.total`, ei data.
   */
  async function fetchOpenCount(): Promise<void> {
    try {
      const res = await authFetch(`${BASE}?status=OPEN&pageSize=1`);
      if (!res.ok) return;
      const body = (await res.json()) as Paginated<ServiceRequest>;
      openCount.value = body.meta?.total ?? 0;
    } catch {
      // Verkkopiikki ei saa tyhjentää merkkiä — vanha luku on
      // parempi arvaus kuin nolla.
    }
  }

  /**
   * Käynnistää avoimien määrän pollauksen (60 s, sama tahti kuin
   * ilmoituksilla). Idempotentti. Kutsuja vastaa oikeustarkistuksesta:
   * ilman `service_request:read` kutsu tuottaisi vain 403-kohinaa.
   */
  function startOpenCountPolling(): void {
    if (pollHandle) return;
    void fetchOpenCount();
    pollHandle = setInterval(() => void fetchOpenCount(), 60_000);
  }

  function stopOpenCountPolling(): void {
    if (pollHandle) {
      clearInterval(pollHandle);
      pollHandle = null;
    }
    openCount.value = 0;
  }

  /**
   * Käsittelymerkintä työjonosta. Kuten paneelissakin: EI muuta
   * sopimusta, vaan kirjaa päätöksen. Kutsuja päivittää listan omilla
   * ehdoillaan; avoimien määrä päivitetään tässä, koska navigaation
   * merkin pitää reagoida heti eikä vasta seuraavalla pollilla.
   */
  async function resolveInQueue(
    id: string,
    payload: { status: "RESOLVED" | "REJECTED"; resolutionNote?: string },
  ): Promise<void> {
    await request<ServiceRequest>(`${BASE}/${id}/resolve`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
    await fetchOpenCount();
  }

  return {
    byContract,
    loading,
    error,
    fetchForContract,
    resolve,
    forContract,
    queue,
    queueMeta,
    queueLoading,
    queueError,
    context,
    openCount,
    fetchQueue,
    contextFor,
    fetchOpenCount,
    startOpenCountPolling,
    stopOpenCountPolling,
    resolveInQueue,
  };
});
